import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reportsAPI, profileAPI } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import {
  SparklesIcon,
  BeakerIcon,
  HeartIcon,
  FireIcon,
  MoonIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ScaleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import TimeframeSelector from './TimeframeSelector'
import ReportChatPanel from './ReportChatPanel'
import ComprehensiveAnalysisActions from './ComprehensiveAnalysisActions'
import './print.css'

// VH-55: Section icons for comprehensive report — mapped by recommendation_type / section key
const SECTION_ICONS = {
  body: ScaleIcon,
  supplement: BeakerIcon,
  supplements: BeakerIcon,
  exercise: FireIcon,
  diet: HeartIcon,
  nutrition: HeartIcon,
  lifestyle: MoonIcon,
  bloodwork: AcademicCapIcon,
  medical: AcademicCapIcon,
}

const SECTION_COLORS = {
  body: 'text-pink-600 bg-pink-50',
  supplement: 'text-blue-600 bg-blue-50',
  supplements: 'text-blue-600 bg-blue-50',
  exercise: 'text-orange-600 bg-orange-50',
  diet: 'text-green-600 bg-green-50',
  nutrition: 'text-green-600 bg-green-50',
  lifestyle: 'text-purple-600 bg-purple-50',
  bloodwork: 'text-red-600 bg-red-50',
  medical: 'text-red-600 bg-red-50',
}

const TERMINAL_STATUSES = new Set(['completed', 'failed'])

function RecommendationCard({ rec, t }) {
  const queryClient = useQueryClient()

  const acceptMutation = useMutation({
    mutationFn: () => reportsAPI.acceptRecommendation(rec.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['healthReport'] }),
  })
  const rejectMutation = useMutation({
    mutationFn: () => reportsAPI.rejectRecommendation(rec.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['healthReport'] }),
  })
  const completeMutation = useMutation({
    mutationFn: () => reportsAPI.completeRecommendation(rec.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['healthReport'] }),
  })

  const isCompleted = rec.is_completed === true
  const isAccepted = rec.is_accepted === true
  const isRejected = rec.is_accepted === false

  const busy = acceptMutation.isPending || rejectMutation.isPending || completeMutation.isPending

  return (
    <li className="text-sm text-gray-700 border-l-2 border-purple-200 pl-3 py-1">
      <div className="flex items-start">
        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
        <span className="flex-1">
          {typeof rec === 'string' ? rec : rec.text || rec.description || rec.title}
        </span>
      </div>
      {rec.id && (
        <div className="flex items-center gap-2 mt-1.5 ml-6">
          {isCompleted ? (
            <span className="text-xs text-green-700 font-medium">
              <CheckCircleIcon className="h-3 w-3 inline mr-1" />
              {t('aiAnalysis.recommendation.completed')}
            </span>
          ) : isAccepted ? (
            <>
              <span className="text-xs text-green-700">
                {t('aiAnalysis.recommendation.accepted')}
              </span>
              <button
                type="button"
                onClick={() => completeMutation.mutate()}
                disabled={busy}
                className="text-xs text-purple-600 hover:text-purple-700 underline disabled:opacity-50"
              >
                {t('aiAnalysis.recommendation.complete')}
              </button>
            </>
          ) : isRejected ? (
            <span className="text-xs text-gray-500">
              <XCircleIcon className="h-3 w-3 inline mr-1" />
              {t('aiAnalysis.recommendation.rejected')}
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={() => acceptMutation.mutate()}
                disabled={busy}
                className="text-xs text-green-700 hover:text-green-800 underline disabled:opacity-50"
              >
                {t('aiAnalysis.recommendation.accept')}
              </button>
              <button
                type="button"
                onClick={() => rejectMutation.mutate()}
                disabled={busy}
                className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
              >
                {t('aiAnalysis.recommendation.reject')}
              </button>
            </>
          )}
        </div>
      )}
    </li>
  )
}

function ComprehensiveReportView({ report, t, i18n, reportRef }) {
  const [expandedSections, setExpandedSections] = useState({})

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: prev[section] === false }))
  }

  const locale = i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US'

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    if (score >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  // Map recommendations to sections format
  const sections = report.recommendations?.map((rec, idx) => ({
    id: rec.id ?? `sec-${idx}`,
    section_type: rec.recommendation_type,
    title: rec.title,
    content: rec.description,
    recommendations: rec.action_items || [],
    reasoning: rec.expected_outcome,
    fullRec: rec,
  })) || []

  const healthScore = report.overall_health_score ?? report.health_score

  // Timeframe handling
  const tfStart = report.timeframe_start || report.start_date || report.data_period_start
  const tfEnd = report.timeframe_end || report.end_date || report.data_period_end
  const isLegacy = !report.timeframe_start && !report.timeframe_end
  const days = tfStart && tfEnd
    ? Math.floor((new Date(tfEnd) - new Date(tfStart)) / (1000 * 60 * 60 * 24)) + 1
    : null

  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Report Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
          <h4 className="font-medium text-gray-900 flex items-center">
            <SparklesIcon className="h-5 w-5 text-purple-600 mr-2" />
            {t('aiAnalysis.comprehensiveReport')}
          </h4>
          <div className="flex items-center gap-4">
            {/* VH-83: Print + Download PDF actions */}
            <ComprehensiveAnalysisActions
              report={report}
              reportRef={reportRef}
              status={report.status || 'completed'}
            />
            {healthScore != null && (
              <div className="text-center">
                <span className={`text-3xl font-bold ${getScoreColor(healthScore)}`}>
                  {Math.round(healthScore)}
                </span>
                <span className="text-xs text-gray-500 block">/100</span>
              </div>
            )}
          </div>
        </div>

        {/* Timeframe banner */}
        {tfStart && tfEnd && (
          <div className="text-xs text-gray-600 bg-surface-container/60 rounded px-2 py-1 inline-block">
            {formatDate(tfStart)} – {formatDate(tfEnd)}
            {days && (
              <span className="ml-2 text-gray-500">
                · {t('aiAnalysis.daysCount', { days })}
              </span>
            )}
            {isLegacy && (
              <span className="ml-2 text-gray-400">
                ({t('aiAnalysis.legacyReport')})
              </span>
            )}
          </div>
        )}

        {(report.summary || report.description) && (
          <p className="text-sm text-gray-700 mt-2">{report.summary || report.description}</p>
        )}
      </div>

      {/* Report Sections */}
      {sections.map((section) => {
        const sectionType = section.section_type || 'other'
        const Icon = SECTION_ICONS[sectionType] || DocumentTextIcon
        const colorClass = SECTION_COLORS[sectionType] || 'text-gray-600 bg-gray-50'
        const isExpanded = expandedSections[section.id] !== false

        return (
          <div key={section.id} className="border rounded-lg analysis-section">
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
            >
              <div className="flex items-center">
                <span className={`p-1.5 rounded-lg mr-2 ${colorClass}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="font-medium">{section.title}</span>
              </div>
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t space-y-3 pt-3">
                {/* Current Status */}
                {section.content && (
                  <div className="text-sm text-gray-700">{section.content}</div>
                )}

                {/* Recommendations */}
                {section.recommendations?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {t('aiAnalysis.recommendations')}
                    </p>
                    <ul className="space-y-2">
                      {section.recommendations.map((rec, idx) => {
                        // For the first recommendation we attach the full rec object
                        // so the user can accept/reject/complete the parent recommendation.
                        const recObj =
                          idx === 0 && section.fullRec?.id
                            ? { ...section.fullRec, text: typeof rec === 'string' ? rec : rec.text || rec.description }
                            : rec
                        return <RecommendationCard key={idx} rec={recObj} t={t} />
                      })}
                    </ul>
                  </div>
                )}

                {/* Reasoning */}
                {section.reasoning && (
                  <p className="text-xs text-gray-500 italic mt-2">{section.reasoning}</p>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Metadata */}
      {report.created_at && (
        <p className="text-xs text-gray-400 text-right">
          {t('aiAnalysis.generatedOn')}: {formatDate(report.created_at)}
        </p>
      )}
    </div>
  )
}

function ReportHistory({ reports, onSelect, selectedId, t, i18n }) {
  if (!reports?.length) return null

  const locale = i18n.language === 'de' ? 'de-DE' : i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US'

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })

  return (
    <div className="border rounded-lg p-4 mb-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
        <ClockIcon className="h-4 w-4 mr-1" />
        {t('aiAnalysis.previousReports')}
      </h4>
      <div className="space-y-1">
        {reports.map((report) => {
          const tfStart = report.timeframe_start || report.start_date
          const tfEnd = report.timeframe_end || report.end_date
          const score = report.overall_health_score ?? report.health_score
          return (
            <button
              key={report.id}
              type="button"
              onClick={() => onSelect(report.id)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                selectedId === report.id
                  ? 'bg-purple-50 text-purple-700 font-medium'
                  : 'hover:bg-gray-50 text-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div>{formatDate(report.created_at)}</div>
                  {tfStart && tfEnd && (
                    <div className="text-xs text-gray-400">
                      {formatDate(tfStart)} – {formatDate(tfEnd)}
                    </div>
                  )}
                </div>
                {score != null && (
                  <span className="font-medium">{Math.round(score)}/100</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default function AIBodyAnalysis() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedReportId, setSelectedReportId] = useState(null)
  const [showBodyAnalysis, setShowBodyAnalysis] = useState(false)

  // Timeframe selector state (default last 90 days)
  const [timeframe, setTimeframe] = useState({ start_date: '', end_date: '', valid: false })

  // Elapsed-time counter for generation
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const elapsedTimerRef = useRef(null)

  // VH-83: ref to the comprehensive-report DOM for PDF capture
  const reportRef = useRef(null)

  // VH-55: Fetch existing comprehensive reports
  const { data: reportList } = useQuery({
    queryKey: ['healthReports'],
    queryFn: () => reportsAPI.getAll({ report_type: 'comprehensive' }),
    select: (response) =>
      response.data?.health_reports || response.data?.items || response.data || [],
  })

  // Fetch selected report detail — polls while status is PENDING / PROCESSING
  const { data: selectedReport, isLoading: loadingReport } = useQuery({
    queryKey: ['healthReport', selectedReportId],
    queryFn: () => reportsAPI.getById(selectedReportId),
    select: (response) => response.data,
    enabled: !!selectedReportId,
    refetchInterval: (query) => {
      const data = query.state.data?.data || query.state.data
      const status = (data?.status || '').toLowerCase()
      return status && !TERMINAL_STATUSES.has(status) ? 3000 : false
    },
  })

  const reportStatus = (selectedReport?.status || '').toLowerCase()
  const isGenerating = !!selectedReportId && !TERMINAL_STATUSES.has(reportStatus)
  const isCompleted = reportStatus === 'completed'
  const isFailed = reportStatus === 'failed'

  // Generate comprehensive report
  const generateMutation = useMutation({
    mutationFn: (payload) => reportsAPI.generate(payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['healthReports'] })
      setSelectedReportId(response.data?.id || response.data?.report_id)
    },
  })

  // Manage elapsed timer when generation is in flight
  useEffect(() => {
    const generating = generateMutation.isPending || isGenerating
    if (generating) {
      if (elapsedTimerRef.current == null) {
        setElapsedSeconds(0)
        elapsedTimerRef.current = setInterval(() => {
          setElapsedSeconds((s) => s + 1)
        }, 1000)
      }
    } else if (elapsedTimerRef.current != null) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }
    return () => {
      if (elapsedTimerRef.current != null) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }
  }, [generateMutation.isPending, isGenerating])

  const handleGenerate = () => {
    if (!timeframe.valid) return
    generateMutation.mutate({
      start_date: timeframe.start_date,
      end_date: timeframe.end_date,
    })
  }

  const handleStartFresh = () => {
    setSelectedReportId(null)
    // Scroll user up to the timeframe selector
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Legacy body analysis
  const [bodyAnalysisResult, setBodyAnalysisResult] = useState(null)
  const [formData, setFormData] = useState({
    activity_level: 'moderate',
    goal_type: 'lose_weight',
    target_weight_kg: '',
    waist_cm: '',
  })

  const bodyAnalysisMutation = useMutation({
    mutationFn: (data) => profileAPI.getAIBodyAnalysis(data),
    onSuccess: (response) => {
      setBodyAnalysisResult(response.data)
    },
  })

  const hasRequiredData = user?.height_cm && user?.date_of_birth && user?.gender

  return (
    <div className="space-y-6">
      {/* VH-55: Comprehensive Analysis Section */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SparklesIcon className="h-6 w-6 text-purple-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                {t('aiAnalysis.comprehensiveTitle')}
              </h3>
            </div>
          </div>
        </div>

        <div className="card-body">
          <p className="text-sm text-gray-600 mb-4">{t('aiAnalysis.comprehensiveDesc')}</p>

          {/* Timeframe selector */}
          <div className="mb-4">
            <TimeframeSelector
              value={timeframe}
              onChange={setTimeframe}
              disabled={generateMutation.isPending || isGenerating}
            />
          </div>

          {/* Generate Button */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateMutation.isPending || isGenerating || !timeframe.valid}
            className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-colors mb-4 ${
              generateMutation.isPending || isGenerating || !timeframe.valid
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-on-neutral'
            }`}
          >
            {generateMutation.isPending || isGenerating ? (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                {t('aiAnalysis.generating')}
              </>
            ) : (
              <>
                <SparklesIcon className="h-5 w-5 mr-2" />
                {t('aiAnalysis.generateReport')}
              </>
            )}
          </button>

          {/* Loading indicator with progress messages */}
          {(generateMutation.isPending || isGenerating) && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-4 border-purple-600 border-t-transparent mr-3" />
                <div>
                  <p className="text-sm font-medium text-purple-700">
                    {t('aiAnalysis.analyzingData')}
                  </p>
                  <p className="text-xs text-purple-600">
                    {t('aiAnalysis.analyzeWait')}
                    <span className="ml-2">
                      {t('aiAnalysis.elapsedTime', { seconds: elapsedSeconds })}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Generation error (request layer) */}
          {generateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4 flex items-start justify-between">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                <span>
                  {generateMutation.error?.response?.data?.detail ||
                    t('common.error', 'An error occurred')}
                </span>
              </div>
              <button
                type="button"
                onClick={handleGenerate}
                className="text-red-700 underline text-sm ml-3 flex-shrink-0"
              >
                {t('aiAnalysis.retry')}
              </button>
            </div>
          )}

          {/* Report failed (status=failed from polling) */}
          {isFailed && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4 flex items-start justify-between">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium">{t('aiAnalysis.reportFailed')}</p>
                  {selectedReport?.error_message && (
                    <p className="text-xs mt-1">{selectedReport.error_message}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleGenerate}
                className="text-red-700 underline text-sm ml-3 flex-shrink-0"
              >
                {t('aiAnalysis.retry')}
              </button>
            </div>
          )}

          {/* Report History */}
          <ReportHistory
            reports={reportList}
            onSelect={setSelectedReportId}
            selectedId={selectedReportId}
            t={t}
            i18n={i18n}
          />

          {/* Selected Report */}
          {loadingReport && !selectedReport && (
            <div className="animate-pulse space-y-4">
              <div className="h-24 bg-gray-200 rounded" />
              <div className="h-16 bg-gray-200 rounded" />
            </div>
          )}

          {selectedReport && isCompleted && (
            <>
              <ComprehensiveReportView
                report={selectedReport}
                t={t}
                i18n={i18n}
                reportRef={reportRef}
              />

              {/* Conversational follow-up dialog — wrapped so the print
                  stylesheet (VH-83) can hide it via .followup-chat. */}
              <div className="followup-chat">
                <ReportChatPanel
                  reportId={selectedReport.id}
                  onGenerateNew={handleStartFresh}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Legacy: Quick Body Analysis (collapsible) */}
      <div className="card">
        <button
          type="button"
          onClick={() => setShowBodyAnalysis(!showBodyAnalysis)}
          className="card-header w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center">
            <BeakerIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              {t('profile.aiAnalysis.title', 'Quick Body Analysis')}
            </h3>
          </div>
          {showBodyAnalysis ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {showBodyAnalysis && (
          <div className="card-body">
            {!hasRequiredData && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
                  <div>
                    <p className="text-yellow-700 font-medium">
                      {t('profile.aiAnalysis.missingData', 'Complete your profile first')}
                    </p>
                    <p className="text-sm text-yellow-600 mt-1">
                      {t(
                        'profile.aiAnalysis.missingDataDesc',
                        'Height, weight, date of birth, and gender are required.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!bodyAnalysisResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">
                      {t('profile.activityLevel', 'Activity Level')}
                    </label>
                    <select
                      value={formData.activity_level}
                      onChange={(e) =>
                        setFormData({ ...formData, activity_level: e.target.value })
                      }
                      className="form-select"
                    >
                      {['sedentary', 'light', 'moderate', 'active', 'very_active'].map((val) => (
                        <option key={val} value={val}>
                          {t(`profile.activityLevels.${val}`, val)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">{t('profile.goalType', 'Goal')}</label>
                    <select
                      value={formData.goal_type}
                      onChange={(e) => setFormData({ ...formData, goal_type: e.target.value })}
                      className="form-select"
                    >
                      {['lose_weight', 'maintain', 'gain_muscle'].map((val) => (
                        <option key={val} value={val}>
                          {t(`profile.goalTypes.${val}`, val)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    bodyAnalysisMutation.mutate({
                      activity_level: formData.activity_level,
                      goal_type: formData.goal_type,
                      target_weight_kg: formData.target_weight_kg
                        ? parseFloat(formData.target_weight_kg)
                        : null,
                      waist_cm: formData.waist_cm ? parseFloat(formData.waist_cm) : null,
                    })
                  }
                  disabled={bodyAnalysisMutation.isPending || !hasRequiredData}
                  className="btn-primary w-full"
                >
                  {bodyAnalysisMutation.isPending ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                      {t('profile.aiAnalysis.analyzing', 'Analyzing...')}
                    </>
                  ) : (
                    t('profile.aiAnalysis.analyze', 'Analyze with AI')
                  )}
                </button>
              </div>
            )}

            {bodyAnalysisResult && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <p className="text-gray-700">{bodyAnalysisResult.executive_summary}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setBodyAnalysisResult(null)}
                  className="btn-secondary"
                >
                  {t('profile.aiAnalysis.newAnalysis', 'Run New Analysis')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
